<script setup>
import { computed, ref, watch } from 'vue'
import { getIcon } from '@/tiptap/constants/icons'
import { useLocale } from '@/tiptap/locales'

const props = defineProps({
  value: { type: String, default: undefined },
  target: { type: String, default: '_blank' }, // '_self' | '_blank'
  editor: { type: Object, required: true },
  destroy: { type: Function, default: undefined },
})

const { t } = useLocale()

const generateLinkAttrs = () => ({ href: '', target: '_blank' })

const attrs = ref(generateLinkAttrs())
const dialog = ref(false)

const isDisabled = computed(() => {
  const { href, target } = attrs.value
  if (!href) return true
  return props.value === href && props.target === target
})

function apply() {
  let { href, target } = attrs.value

  // Normalize: add https:// if no protocol is specified
  if (href && !/^https?:\/\//i.test(href)) {
    href = `https://${href}`
  }

  if (href) {
    props.editor.chain().focus().extendMarkRange('link').setLink({ href, target }).run()
  }
  close()
}

function close() {
  dialog.value = false
  attrs.value = generateLinkAttrs()
  setTimeout(() => props.destroy && props.destroy(), 300)
}

watch(dialog, (val) => {
  if (!val) return
  attrs.value = { href: props.value, target: props.target }
})
</script>

<template>
  <VDialog v-model="dialog" max-width="400" activator="parent" @click:outside="close">
    <VCard>
      <VToolbar class="px-6" density="compact">
        <span class="headline">{{ t('editor.link.dialog.title') }}</span>
        <VSpacer />
        <VBtn class="mx-0" icon @click="close">
          <VIcon :icon="getIcon('close')" />
        </VBtn>
      </VToolbar>

      <VCardText>
        <VTextField v-model="attrs.href" :label="t('editor.link.dialog.link')" autofocus />
      </VCardText>

      <VCardActions>
        <VBtn :disabled="isDisabled" @click="apply">
          {{ t('editor.link.dialog.button.apply') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>